import { useEffect, useState } from "react";
import { CalendarCheck2, CalendarHeart, CalendarRange, CheckCircle2, CircleHelp, KeyRound, Link2, Loader2, RefreshCw, ShieldCheck, TriangleAlert, Unplug, User } from "lucide-react";
import { api, apiErrorDetail } from "@/lib/api";
import { SITE_BASE_URL } from "@/lib/site";

function fmtRome(iso) {
  if (!iso) return "—";
  try {
    return new Intl.DateTimeFormat("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit", timeZone: "Europe/Rome" }).format(new Date(iso));
  } catch (e) {
    return iso;
  }
}

/* Calendario e sincronizzazione: DUE collegamenti Google indipendenti.
   A) Calendario personale: solo lettura occupato/libero, blocca gli slot pubblici. Mai dettagli privati.
   B) Calendario K Nutrition: eventi degli appuntamenti confermati, aggiornati e rimossi automaticamente. */
export default function GoogleTab({ flash }) {
  const [status, setStatus] = useState(null);
  const [form, setForm] = useState(null);
  const [creds, setCreds] = useState({ clientId: "", clientSecret: "" });
  const [busy, setBusy] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [connecting, setConnecting] = useState("");

  const load = () => {
    api
      .get("/admin/google/status")
      .then(({ data }) => {
        setStatus(data);
        setForm({
          syncEnabled: data.syncEnabled,
          blockPersonalBusy: data.blockPersonalBusy,
          fallbackMode: data.fallbackMode || "safe",
          personalCalendarId: data.personalCalendarId || "primary",
          professionalCalendarId: data.professionalCalendarId || "primary",
        });
      })
      .catch((e) => flash(apiErrorDetail(e), false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/admin/google/settings", form);
      flash("Impostazioni del calendario salvate");
      load();
    } catch (e) {
      flash(apiErrorDetail(e), false);
    } finally {
      setBusy(false);
    }
  };

  const saveCreds = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/admin/google/credentials", creds);
      flash(data.configured ? "Chiavi Google salvate: puoi collegare i calendari" : "Chiavi salvate ma incomplete");
      setCreds({ clientId: "", clientSecret: "" });
      load();
    } catch (e) {
      flash(apiErrorDetail(e), false);
    } finally {
      setBusy(false);
    }
  };

  const connect = async (kind) => {
    setConnecting(kind);
    try {
      const { data } = await api.get(`/admin/google/connect`, { params: { kind } });
      window.location.href = data.url;
    } catch (e) {
      flash(apiErrorDetail(e), false);
      setConnecting("");
    }
  };

  const disconnect = async (kind) => {
    if (!window.confirm(kind === "personale" ? "Scollegare il calendario personale?" : "Scollegare il calendario K Nutrition? Gli eventi già creati restano su Google.")) return;
    try {
      await api.post("/admin/google/disconnect", { kind });
      flash("Account scollegato");
      load();
    } catch (e) {
      flash(apiErrorDetail(e), false);
    }
  };

  const syncNow = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/admin/google/sync");
      flash(`Sincronizzati: ${data.created} creati, ${data.updated} aggiornati, ${data.deleted} rimossi${data.errors ? `, ${data.errors} errori` : ""}`);
      load();
    } catch (e) {
      flash(apiErrorDetail(e), false);
    } finally {
      setSyncing(false);
    }
  };

  if (!status || !form) return <p className="text-charcoal/50 text-sm py-10">Caricamento…</p>;

  const connCard = (kind, title, desc, icon) => {
    const c = status[kind];
    return (
      <div className="bg-white rounded-[24px] shadow-soft p-6 flex flex-col" data-testid={`google-card-${kind}`}>
        <div className="flex items-start gap-3 mb-3">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${c.connected ? "bg-[#1f7a4d]/10 text-[#1f7a4d]" : "bg-taupe/15 text-charcoal/60"}`}>
            {icon}
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-medium text-sm">{title}</p>
            <p className="text-[13px] text-charcoal/60 mt-0.5">{desc}</p>
          </div>
        </div>
        {c.connected ? (
          <div className="text-[13px] space-y-1 flex-1">
            <p className="flex items-center gap-1.5 text-[#1f7a4d]" data-testid={`google-state-${kind}`}>
              <CheckCircle2 size={14} /> Collegato: {c.email || "account Google"}
            </p>
            <p className="text-charcoal/60">{kind === "pro" ? `Ultima sincronizzazione: ${fmtRome(c.lastSync)}` : "Lettura solo occupato/libero"}</p>
            {c.lastError && (
              <p className="rounded-xl bg-red-50 border border-red-200 text-red-700 px-3 py-2 flex items-start gap-1.5" data-testid={`google-error-${kind}`}>
                <TriangleAlert size={13} className="mt-0.5 shrink-0" /> {c.lastError}
              </p>
            )}
          </div>
        ) : (
          <p className="text-[13px] text-charcoal/60 flex-1">Non collegato. Serve l'autorizzazione con il tuo account Google.</p>
        )}
        <div className="mt-4">
          {c.connected ? (
            <button
              onClick={() => disconnect(kind)}
              data-testid={`google-disconnect-${kind}`}
              className="inline-flex items-center gap-2 rounded-full border border-red-200 text-red-600 text-[12px] uppercase tracking-widest2 font-medium px-5 py-2.5 hover:bg-red-50 transition-colors"
            >
              <Unplug size={13} /> Scollega
            </button>
          ) : (
            <button
              onClick={() => connect(kind)}
              disabled={!status.configured}
              data-testid={`google-connect-${kind}`}
              className="inline-flex items-center gap-2 rounded-full bg-deeptaupe text-ivory text-[12px] uppercase tracking-widest2 font-medium px-6 py-2.5 hover:bg-charcoal transition-colors disabled:opacity-40"
            >
              {connecting === kind ? <Loader2 size={13} className="animate-spin" /> : <Link2 size={13} />} Collega {kind === "personale" ? "il personale" : "K Nutrition"}
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-display text-2xl lg:text-3xl flex items-center gap-3">
          <CalendarCheck2 size={24} className="text-deeptaupe" /> Calendario e sincronizzazione
        </h2>
        <p className="text-sm text-charcoal/60 mt-2 max-w-2xl">
          Due collegamenti indipendenti: il tuo calendario personale protegge gli orari pubblici, il calendario
          K Nutrition riceve gli appuntamenti confermati. Fuso orario: Europe/Rome.
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {connCard("personale", "Collega calendario personale", "Leggo SOLO quando sei occupata: mai nomi, descrizioni o dettagli degli eventi privati.", <User size={18} />)}
        {connCard("pro", "Collega calendario K Nutrition", "Crea e aggiorna gli eventi degli appuntamenti confermati; li rimuove se annullati. Sola scrittura, mai duplicati.", <CalendarHeart size={18} />)}
      </div>

      <div className="bg-white rounded-[24px] shadow-soft p-6" data-testid="google-credentials-card">
        <p className="font-medium text-sm flex items-center gap-2 mb-1">
          <KeyRound size={15} className="text-deeptaupe" /> Chiavi OAuth Google
        </p>
        <p className="text-[13px] text-charcoal/60 mb-4">
          {status.envConfigured
            ? "Attive dalle variabili d'ambiente (più sicure). Puoi comunque sostituirle qui: varranno finché l'ambiente non ne definisce di proprie."
            : status.configured
              ? "Attive dalle chiavi salvate qui nel pannello (il secret è scritto una sola volta e mai mostrato di nuovo)."
              : "Inserisci qui ID client e Secret client: restano solo sul server, mai in chat o nel codice. Con le chiavi salvate puoi collegare entrambi i calendari."}
        </p>
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block" htmlFor="g-clientid">
            <span className="font-medium text-sm">Client ID</span>
            <input
              id="g-clientid"
              type="text"
              value={creds.clientId}
              onChange={(e) => setCreds({ ...creds, clientId: e.target.value })}
              placeholder="es. 1234567890-abc.apps.googleusercontent.com"
              data-testid="google-client-id"
              className="mt-1.5 w-full rounded-2xl border border-taupe/40 px-4 py-3 text-sm bg-transparent"
            />
          </label>
          <label className="block" htmlFor="g-secret">
            <span className="font-medium text-sm">Client secret {status.envConfigured ? "" : "(nuovo, ruotato)"}</span>
            <input
              id="g-secret"
              type="password"
              value={creds.clientSecret}
              onChange={(e) => setCreds({ ...creds, clientSecret: e.target.value })}
              placeholder={status.envConfigured ? "già attivo dalle variabili d'ambiente" : "incollo qui, non appare mai più"}
              data-testid="google-client-secret"
              className="mt-1.5 w-full rounded-2xl border border-taupe/40 px-4 py-3 text-sm bg-transparent"
            />
          </label>
        </div>
        <button
          onClick={saveCreds}
          disabled={busy || (!creds.clientId && !creds.clientSecret)}
          data-testid="google-save-credentials"
          className="mt-4 inline-flex items-center gap-2 rounded-full border border-deeptaupe/40 text-deeptaupe text-[12px] uppercase tracking-widest2 font-medium px-6 py-3 hover:bg-peach transition-colors disabled:opacity-40"
        >
          <KeyRound size={13} /> Salva chiavi
        </button>
      </div>

      <div className="bg-white rounded-[24px] shadow-soft p-6" data-testid="google-sync-card">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
          <p className="font-display text-xl flex items-center gap-2"><CalendarRange size={18} className="text-deeptaupe" /> Sincronizzazione</p>
          <button
            onClick={syncNow}
            disabled={syncing || !status.pro.connected}
            data-testid="google-sync-now"
            title={status.pro.connected ? "" : "Collega prima il calendario K Nutrition"}
            className="inline-flex items-center gap-2 rounded-full bg-deeptaupe text-ivory text-[12px] uppercase tracking-widest2 font-medium px-6 py-3 hover:bg-charcoal transition-colors disabled:opacity-40"
          >
            {syncing ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Sincronizza ora
          </button>
        </div>
        <div className="space-y-4 text-sm">
          <label className="flex items-center justify-between gap-4" htmlFor="g-sync">
            <span className="flex-1">Sincronizza gli appuntamenti confermati sul calendario professionale<span className="block text-charcoal/60 text-[13px]">Creazione e aggiornamento automatici; annullamenti rimossi. K Nutrition resta la fonte ufficiale degli stati.</span></span>
            <input id="g-sync" type="checkbox" checked={form.syncEnabled} onChange={(e) => setForm({ ...form, syncEnabled: e.target.checked })} data-testid="google-sync-enabled" className="w-5 h-5 accent-[#6B5C50]" />
          </label>
          <label className="flex items-center justify-between gap-4" htmlFor="g-busy">
            <span className="flex-1">Blocca gli orari del calendario personale<span className="block text-charcoal/60 text-[13px]">Gli impegni privati tolgono quegli orari dalla prenotazione pubblica, in automatico.</span></span>
            <input id="g-busy" type="checkbox" checked={form.blockPersonalBusy} onChange={(e) => setForm({ ...form, blockPersonalBusy: e.target.checked })} data-testid="google-busy-block" className="w-5 h-5 accent-[#6B5C50]" />
          </label>
          <div>
            <p className="font-medium mb-2">Se il calendario personale non è verificabile:</p>
            <div className="grid gap-3 sm:grid-cols-2">
              <label className={`rounded-2xl border p-4 cursor-pointer ${form.fallbackMode === "safe" ? "border-deeptaupe bg-peach/40" : "border-taupe/30"}`} data-testid="google-fallback-safe">
                <input type="radio" name="fallback" className="mr-2 accent-[#6B5C50]" checked={form.fallbackMode === "safe"} onChange={() => setForm({ ...form, fallbackMode: "safe" })} />
                <b>Sicuro (consigliato)</b>
                <span className="block text-charcoal/60 text-[13px] mt-1">Gli orari vengono sospesi finché la verifica non torna: nessun rischio di doppia prenotazione.</span>
              </label>
              <label className={`rounded-2xl border p-4 cursor-pointer ${form.fallbackMode === "warn" ? "border-deeptaupe bg-peach/40" : "border-taupe/30"}`} data-testid="google-fallback-warn">
                <input type="radio" name="fallback" className="mr-2 accent-[#6B5C50]" checked={form.fallbackMode === "warn"} onChange={() => setForm({ ...form, fallbackMode: "warn" })} />
                <b>Apri con avviso</b>
                <span className="block text-charcoal/60 text-[13px] mt-1">Gli orari restano prenotabili con avviso sul sito che la disponibilità è in verifica.</span>
              </label>
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block" htmlFor="g-personal">
              <span className="font-medium text-sm">ID calendario personale</span>
              <input id="g-personal" type="text" value={form.personalCalendarId} onChange={(e) => setForm({ ...form, personalCalendarId: e.target.value })} data-testid="google-personal-cal" className="mt-1.5 w-full rounded-2xl border border-taupe/40 px-4 py-3 text-sm bg-transparent" placeholder="primary" />
              <span className="block text-charcoal/60 text-[13px] mt-1">"primary" è il calendario principale dell'account personale collegato.</span>
            </label>
            <label className="block" htmlFor="g-pro">
              <span className="font-medium text-sm">ID calendario professionale K Nutrition</span>
              <input id="g-pro" type="text" value={form.professionalCalendarId} onChange={(e) => setForm({ ...form, professionalCalendarId: e.target.value })} data-testid="google-pro-cal" className="mt-1.5 w-full rounded-2xl border border-taupe/40 px-4 py-3 text-sm bg-transparent" placeholder="primary o id@group.calendar.google.com" />
              <span className="block text-charcoal/60 text-[13px] mt-1">Suggerimento: crea un calendario "K Nutrition" sull'account dedicato e incollane l'ID; condividilo con il tuo account personale per vederlo anche nell'app Google Calendar insieme al personale.</span>
            </label>
          </div>
          <button
            onClick={save}
            disabled={busy}
            data-testid="google-save-settings"
            className="rounded-full bg-deeptaupe text-ivory text-[12px] uppercase tracking-widest2 font-medium px-8 py-3.5 hover:bg-charcoal transition-colors disabled:opacity-60"
          >
            {busy ? "Salvataggio…" : "Salva impostazioni"}
          </button>
        </div>
      </div>

      <div className="bg-white rounded-[24px] shadow-soft p-6" data-testid="google-setup">
        <p className="font-medium text-sm flex items-center gap-2 mb-3"><ShieldCheck size={15} className="text-deeptaupe" /> Configurazione Google Cloud e dominio futuro</p>
        <details className="text-sm text-charcoal/70" open={!status.configured}>
          <summary className="cursor-pointer font-medium text-charcoal">I passaggi completi (Cloud Console, test utenti, domini)</summary>
          <ol className="list-decimal ml-5 mt-3 space-y-1.5">
            <li>API e servizi → Libreria → <b>Google Calendar API</b> abilitata (già fatto).</li>
            <li>Schermata consenso OAuth in modalità <b>Testing</b>: aggiungi come <b>Utenti di test</b> entrambi i tuoi account Google (personale e K Nutrition) — solo quelli potranno collegarsi finché l'app non è pubblicata.</li>
            <li>Origini JavaScript autorizzate: <code className="text-[11px] bg-ivory rounded px-1">{SITE_BASE_URL}</code></li>
            <li>URI di reindirizzamento attuale: <code className="text-[11px] bg-ivory rounded px-1">{SITE_BASE_URL}/api/oauth/calendar/callback</code></li>
            <li><b>Quando attiverai il dominio vero</b> aggiungi in Google Cloud anche: Origine <code className="text-[11px] bg-ivory rounded px-1">https://www.knutrition.it</code> e redirect <code className="text-[11px] bg-ivory rounded px-1">https://www.knutrition.it/api/oauth/calendar/callback</code> — e dimmelo: aggiorno io l'ambiente. Le chiavi inserite qui nel pannello restano valide anche in produzione perché stanno nel database; quelle nelle variabili d'ambiente vanno ridichiarate nell'ambiente di produzione.</li>
            <li>Per vedere i DUE calendari insieme nell'app Google Calendar: imposta il calendario K Nutrition come condivisione verso il tuo account personale (Google Calendar → impostazioni calendario → Condividi con) — oppure collega qui entrambi gli account come sopra.</li>
          </ol>
        </details>
        {!status.configured && (
          <p className="mt-3 text-[13px] text-charcoal/60 flex items-start gap-1.5">
            <CircleHelp size={14} className="mt-0.5 shrink-0 text-taupe" />
            Finché le chiavi non sono salvate, il sito continua a funzionare come sempre: prenotazioni, approvazioni e WhatsApp manuale non cambiano.
          </p>
        )}
      </div>
    </div>
  );
}
